import { useState, useEffect, useCallback } from 'react'
import { error } from '../../../../../utils/logger'
import {
  runPipelineBatch,
  getSkipStages,
  refreshStoredConfig,
  loadGovernanceConfig,
  type PipelineBatchResult,
} from '../../../../../governance'
import { ALL_QUESTIONS_CORE } from '../../../../../content'

/**
 * Inline gate-threshold defaults — mirror DEFAULT_GATE_THRESHOLDS in
 * issueContract.ts. Inlined here to avoid pulling another import path.
 */
const GATE_DEFAULTS = {
  productionMinScore: 70,
  assessmentMinScore: 82,
  reviewCoachSeverityThreshold: 45,
  assessmentMinQuestionsPerFamily: 3,
} as const

export interface PipelineRunState {
  isRunning: boolean
  lastRun: string | null
  runDuration: string | null
  batchResult: PipelineBatchResult | null
}

export interface PipelineRunController extends PipelineRunState {
  revalidate: () => void
}

/**
 * usePipelineRun — owns the pipeline execution lifecycle.
 *
 * Single responsibility:
 *  - Read stored governance config (skip-stages, pedagogy toggle, quality gates)
 *  - Run `runPipelineBatch` over ALL_QUESTIONS_CORE
 *  - Track timing (last run timestamp, duration) for the admin header
 *
 * Auto-runs on mount. Exposes `revalidate` for the manual "Opnieuw valideren"
 * button.
 *
 * Extracted from useGovernanceData (R1) so the run lifecycle is independent
 * of the derivation/view-model logic. Useful for future use cases like
 * comparing two runs side-by-side (snapshot-infra route 1).
 */
export function usePipelineRun(): PipelineRunController {
  const [isRunning, setIsRunning] = useState(true)
  const [lastRun, setLastRun] = useState<string | null>(null)
  const [runDuration, setRunDuration] = useState<string | null>(null)
  const [batchResult, setBatchResult] = useState<PipelineBatchResult | null>(
    null,
  )

  const revalidate = useCallback(() => {
    setIsRunning(true)
    const startTime = Date.now()
    const now = new Date()
    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now
      .getMinutes()
      .toString()
      .padStart(2, '0')}`

    // setTimeout lets React paint the loading state before the heavy run
    setTimeout(async () => {
      try {
        await refreshStoredConfig()
        const skipStages = await getSkipStages()
        const govConfig = await loadGovernanceConfig()
        const skipPedagogy = govConfig.pipelineStages?.pedagogyEnabled === false

        const thresholds = govConfig.qualityGates
          ? {
              productionMinScore:
                govConfig.qualityGates.minQualityScore ??
                GATE_DEFAULTS.productionMinScore,
              assessmentMinScore: Math.max(
                (govConfig.qualityGates.minQualityScore ?? 70) + 12,
                GATE_DEFAULTS.assessmentMinScore,
              ),
              reviewCoachSeverityThreshold:
                GATE_DEFAULTS.reviewCoachSeverityThreshold,
              assessmentMinQuestionsPerFamily:
                GATE_DEFAULTS.assessmentMinQuestionsPerFamily,
            }
          : undefined

        const result = runPipelineBatch(ALL_QUESTIONS_CORE, {
          ...(skipStages.length > 0 ? { skipStages } : {}),
          skipPedagogy,
          ...(thresholds ? { thresholds } : {}),
        })
        const duration = ((Date.now() - startTime) / 1000).toFixed(1)
        setBatchResult(result)
        setLastRun(`${timeStr} (${duration}s)`)
        setRunDuration(`${duration}s`)
      } catch (err) {
        error('Validation failed:', err)
      } finally {
        setIsRunning(false)
      }
    }, 50)
  }, [])

  useEffect(() => {
    revalidate()
  }, [revalidate])

  return { isRunning, lastRun, runDuration, batchResult, revalidate }
}
